<template>
  <section class="section">
    <div class="container">
      <h1 class="title">Terms of Use</h1>
      <p>Updated: December 27, 2022</p>
      <p>
        Welcome to the website of Mile Premier, LLC, dba Porsche Auto Insurance
        (“PAI”). PAI allows users to access information about PAI, as well as
        apply for and receive certain information about insurance products and
        services offered by PAI through its website located at
        www.porscheautoinsurance.com (the “Site”). Insurance policies are not
        issued via the Site, and the issuance of insurance policies by PAI is
        subject to separate terms and conditions for underwriting, policy
        binding and issuance of insurance coverage.
      </p>

      <h2 class="subtitle">1. Acceptance of the Terms.</h2>
      <p>
        Please carefully read the following terms of use and all other rules and
        guidelines that we may communicate to you from time to time through the
        Site, including the Privacy Policy, which are incorporated herein by
        reference (collectively, the “Terms”), as these Terms create a binding
        legal contract between you and PAI. By accessing, using or availing
        yourself of the Site in any manner, you (“You”, or “User”) acknowledge
        that you have read, understood, and agree to be bound by and comply with
        the Terms. If at any time you do not agree to these Terms, you should
        immediately terminate any and all use of the Site. To the extent that
        you download and use any separate software applications from PAI
        (whether directly through the Site or made available to you through a
        third party application marketplace), your use of such applications will
        be governed by the terms of the applicable end user license which will
        be incorporated into and made a part of these Terms.
      </p>

      <h2 class="subtitle">2. Accessing the Site.</h2>
      <p>i. Eligibility.</p>
      <p>
        You must be an individual at least 15 years old to use the Site. Certain
        plans or activities presented through the Site may have other age
        restrictions. By using the Site, you represent that you are at least the
        minimum required age (in no case less than 15 years old) and in any case
        that you are fully able and competent to enter into the terms,
        conditions, obligations, affirmations, representations and warranties
        set forth in these Terms. Further, by using or registering for the Site,
        you agree to abide by and comply with these Terms, and you acknowledge
        that you are accepting the benefit of the Site, subject to these Terms.
      </p>
      <p>ii. Access.</p>
      <p>
        PAI grants you permission to access and use the Site as set forth in
        these Terms consistent with the intended features of the Site, provided
        that: (i) you use the Site solely for your personal, private,
        noncommercial purposes; (ii) you do not reproduce, modify, publicly
        display, publicly perform, or distribute any part of the Site, including
        any materials obtained from or included within the Site; and (iii) you
        do not engage in any of the prohibited uses described below.
      </p>
      <p>iii. Site Monitoring</p>
      <p>
        You acknowledge and agree that PAI may monitor and record information
        regarding your usage of the Site via “cookies”, programs, or other means
        for the purposes of improving its products and services, as well as
        analyzing and improving the performance and usability of the Site.
      </p>

      <h2 class="subtitle">3. Geographic Limitations.</h2>
      <p>
        The insurance products and services offered by PAI are subject to
        regulatory approval. Therefore, use of the Site by residents of states
        or jurisdictions in which PAI is not yet licensed to transact insurance
        business is limited to informational purposes only. Check back
        periodically to see if your state or jurisdiction has been added. Use of
        the Site to obtain insurance quotes in non-licensed states or
        jurisdictions is not permitted, and quotes that might be so obtained are
        void.
      </p>

      <h2 class="subtitle">4. Issuance of Insurance Policies.</h2>
      <p>
        Upon receiving a quote for insurance on the Site, an insurance policy
        will not be issued until you comply with all the terms and conditions of
        policy issuance. For example, submitting payment authorization and
        electronic signing of the policy documents is not sufficient to obtain
        an insurance policy because a policy cannot be issued until PAI has
        received a validated mileage reading for your vehicle. In addition, all
        PAI insurance policies are subject to traditional underwriting
        guidelines and insurance company procedures. In the event of a conflict
        between these Terms and the terms contained in your insurance policy,
        the conflicting terms of the insurance policy shall govern.
      </p>

      <h2 class="subtitle">5. Ownership; Proprietary Rights.</h2>
      <p>
        The Site is owned and operated by PAI. The visual interfaces, graphics,
        design, compilation, information, computer code, products, software
        (including any downloadable software), services, and all other elements
        of the Site provided by PAI (“Site Content”) are protected by United
        States copyright, trade dress, patent, and trademark laws, international
        conventions, and all other relevant intellectual property and
        proprietary rights, and applicable laws. All Site Content is the
        copyrighted property of PAI or its subsidiaries or affiliated companies
        and/or third parties, except for User Content, to which you retain your
        copyrights. All trademarks, service marks, and trade names are to the
        property of PAI or its affiliates and/or third parties. You agree not to
        sell, license, distribute, copy, modify, publicly perform or display,
        transmit, publish, edit, adapt, create derivative works from, or
        otherwise make unauthorized use of the Site Content, and nothing herein
        shall be interpreted to grant you any right or license under any
        intellectual property rights of PAI or any third party. In the event you
        choose to submit ideas or materials to PAI ("Submissions"), such as for
        marketing, contests, products, advertising, processes, promotions,
        trademarks, logos, services, artwork, photographic images, or videos,
        you grant to PAI and the Porsche Group (as defined below) an
        irrevocable, world-wide, perpetual, royalty-free license and right to
        use, modify, publish, disclose, or otherwise make use of the Submission
        at PAI's sole discretion.
      </p>

      <h2 class="subtitle">6. Prohibited Uses of Site.</h2>
      <p>
        You agree not to use the Site or any aspect or feature thereof for any
        unlawful purpose or in any way that might harm, damage, or disparage any
        other party. Without limiting the preceding sentence, you agree that you
        will not attempt or complete any of the following:
      </p>
      <p>
        1. Reproduce, duplicate, copy, sell, trade, resell, distribute or
        exploit, any part of the Site, use of the Site, access to the Site, or
        content obtained through the Site (including without limitation Site
        Content, Third Party Content, and User Content), for any purpose other
        than for your personal, noncommercial purposes;
      </p>
      <p>
        2. Access or use the Site for any commercial or business purpose,
        including without limitation for comparative or competitive research
        purposes;
      </p>
      <p>
        3. Remove, circumvent, disable, damage or otherwise interfere with any
        security-related features of the Site, or features that enforce
        limitations on the use of the Site or any content therein;
      </p>
      <p>
        4. Use any manual or automated means to extract and/or compile content
        from the Site for any commercial purpose or otherwise;
      </p>
      <p>
        5. Interfere with or disrupt the Site, networks or servers connected to
        the Site, or violate the regulations, policies or procedures of such
        networks or servers;
      </p>
      <p>
        6. Undertake, cause, permit or authorize the modification, creation of
        derivative works, translation, reverse engineering, decompiling,
        disassembling or hacking of all or part of the Site, Site Content, Third
        Party Content, or User Content, or any other part thereof, except and
        solely to the extent permitted by law, or otherwise attempt to use or
        access any of the Site other than as intended;
      </p>
      <p>
        7. Use the Site in any manner whatsoever that could lead to a violation
        of any federal, state or local laws, rules or regulations.
      </p>

      <h2 class="subtitle">
        7. Indemnity, No Warranty & Limitation of Liability.
      </h2>
      <p>
        You agree to indemnify and hold harmless PAI and its parent,
        subsidiaries, affiliates or any related companies, licensors and
        suppliers, and their respective directors, officers, employees, agents,
        representatives, customers, and contractors (the “PAI Group”), as well
        as Porsche Financial Services, Inc. (“PFS”) and Porsche Cars North
        America, Inc. (“PCNA”) and their parents, subsidiaries, affiliates or
        any related companies, licensors and suppliers, and their respective
        directors, officers, employees, agents, representatives, customers, and
        contractors (the “Porsche Group”) from all damages, injuries,
        liabilities, costs, fees and expenses (including, but not limited to,
        legal and accounting fees) arising from or in any way related to your
        use or misuse of the Site.
      </p>

      <p>
        The contents of the Site are provided “as is” and without any warranties
        or representations of any kind, either express or implied. To the
        fullest extent permitted by law, the PAI Group and the Porsche Group
        completely and expressly disclaims any and all warranties with respect
        to the Site. This includes, without limitation, implied warranties of
        merchantability, fitness for a particular purpose and non-infringement.
        The PAI Group and the Porsche Group are not responsible for damages or
        injuries that result from your access to or use of the Site, including
        without limitation (a) the use or inability to use the Site, (b) the use
        of any sites hyperlinked via the Site, (c) error or omissions within the
        Site, (d) functional failure of the Site, (e) inability to access the
        Site, (f) system virus or malicious code, (g) network delays or failure,
        or (h) any other website failures or lack of performance of the Site.
      </p>

      <p>
        To the fullest extent permitted by law, the PAI Group and the Porsche
        Group are not liable for consequential, special or incidental damages of
        any kind whether or not resulting in any injury, damage, claim or loss,
        whether based in tort, contract, strict liability or other legal or
        equitable basis which results from or arises out of the use of (or
        inability to use) the Site or contents found in the Site; unless such
        damages result from the gross negligence or intentional acts of the PAI
        Group and/or the Porsche Group, respectively.
      </p>

      <h2 class="subtitle">8. Jurisdiction / Enforceability.</h2>
      <p>
        These Terms and the use of the Site shall be governed by and construed
        in accordance with the laws of the State of Georgia, USA, without giving
        effect to any principles of conflicts of laws. Any dispute concerning
        this site shall be subject to the exclusive venue of a court of
        competent jurisdiction in Fulton County, Georgia. In the event, that any
        provision of these Terms should be held invalid, unlawful or for any
        reason unenforceable, then the invalid, unlawful or unenforceable
        provision shall be severable from the remaining provisions. Such
        invalid, unlawful or unenforceable provision shall not affect the
        validity or enforceability of the remaining provisions.
      </p>

      <h2 class="subtitle">9. Your Responsibilities.</h2>
      <p>
        By accessing the Site, you consent that PAI has the right to deny the
        use of all or any portion of the Site in the event you violate (as PAI
        determines in its sole discretion) any provision of these Terms. PAI and
        the Porsche Group reserve the right to seek all remedies available at
        law and in equity in the event these Terms are violated. It is your
        responsibility to protect your username and password from unauthorized
        access or use, and agree that you are solely responsible for any use of
        or access to your account in the event your login information is
        intentionally or accidentally shared by you. In the event of
        unauthorized access to your account via the Site, please notify us
        immediately via telephone at
        <nobr
          ><a
            style="text-decoration:underline;color:#000"
            href="tel:1-800-400-4128"
            >800-400-4128</a
          ></nobr
        >
      </p>

      <h2 class="subtitle">10. Modification of the Terms.</h2>
      <p>
        PAI reserves the right to update or modify the Terms at any time without
        prior notice, and such changes will be effective immediately upon being
        posted through the Site, except as set forth below. These Terms will
        identify the date of last update. Your use of any Site following any
        such change constitutes your agreement to be bound by the modified
        Terms. Material changes to these Terms will be effective upon the
        earlier of (i) your first use of the Site with actual notice of such
        change, or (ii) 30 days from posting of such change. Disputes arising
        under these Terms will be resolved in accordance with the version of the
        Terms in place at the time the dispute arose. We encourage you to review
        these Terms frequently to stay informed of the latest modifications.
      </p>
    </div>
  </section>
</template>

<script>
export default {
  name: 'TermsOfUse'
};
</script>
